import { Box, Paper, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { money } from '../../lib/format';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { StatCard, StatGrid, StatusChip } from '../../ui/primitives';
import RemittancesPanel from './RemittancesPanel';

export default function WithholdingTab() {
  const scope = useTenantScope();
  const rows = useMemo(() => {
    const byHolder = new Map<string, number>();
    for (const entry of scope.ledger.filter(
      (item) => item.type === 'WITHHOLDING' && item.effectiveDate >= '2026-07-01',
    )) {
      byHolder.set(entry.holderId, (byHolder.get(entry.holderId) ?? 0) + entry.amount);
    }
    return Array.from(byHolder.entries()).map(([holderId, amount]) => ({ holderId, amount }));
  }, [scope.ledger]);
  const tfnTotal = sumBy(
    rows.filter((row) => scope.holderById.get(row.holderId)?.residency === 'AU'),
    (row) => row.amount,
  );
  const nrTotal = sumBy(rows, (row) => row.amount) - tfnTotal;
  return (
    <Box>
      <StatGrid>
        <StatCard label="TFN withholding FY2027" value={money(tfnTotal)} tone="warning" />
        <StatCard label="Non-resident withholding FY2027" value={money(nrTotal)} />
        <StatCard label="Next remittance to ATO" value="21 Oct 2026" hint="September withholding" />
      </StatGrid>
      <RemittancesPanel />
      <Paper variant="outlined">
        <DataTable
          rows={rows}
          rowKey={(row) => row.holderId}
          dense
          defaultSort={{ key: 'amount', direction: 'desc' }}
          emptyMessage="No withholding this financial year"
          columns={[
            {
              key: 'holder',
              label: 'Holder',
              render: (row) => scope.holderById.get(row.holderId)?.name,
            },
            {
              key: 'reason',
              label: 'Reason',
              render: (row) => {
                const holder = scope.holderById.get(row.holderId);
                return holder?.residency === 'AU' ? (
                  <StatusChip status="NOT_QUOTED" />
                ) : (
                  <StatusChip status="REPORTABLE" label={`Non-resident (${holder?.residency})`} />
                );
              },
            },
            {
              key: 'amount',
              label: 'Withheld FY2027',
              align: 'right',
              sortValue: (row) => row.amount,
              render: (row) => money(row.amount, true),
            },
          ]}
        />
      </Paper>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
        Withholding stops automatically once a holder quotes a TFN or ABN in the app; amounts
        already withheld are credited in their tax return.
      </Typography>
    </Box>
  );
}
